import { css, cx } from '@repo/pandacss/styled-system/css';
import type { HTMLAttributes, ReactNode } from 'react';
import type { CommonProps } from '../types';

export type CustomizedSummaryDetailsProps = CommonProps & {
  variant: 'customizedSummary';
  CollapseDetailsComponent: ReactNode;
  collapseDetailsParentProps?: HTMLAttributes<HTMLSpanElement> & { ['data-testid']?: string };
  ExpandDetailsComponent: ReactNode;
  expandDetailsParentProps?: HTMLAttributes<HTMLSpanElement> & { ['data-testid']?: string };
};

const detailsClass = css({
  position: 'relative',
  '&[open] > summary > .open-summary': {
    display: 'none',
  },
  '&[open] > summary > .close-summary': {
    display: 'flex',
  },
});

const summaryClass = css({
  listStyleType: 'none',
  cursor: 'pointer',
  '&::-webkit-details-marker': {
    display: 'none',
  },
  '&::marker': {
    display: 'none',
  },
});

const closeSummaryClass = css({
  display: 'none',
});

export function CustomizedSummaryDetails({
  children,
  CollapseDetailsComponent,
  collapseDetailsParentProps = {},
  detailsProps = {},
  ExpandDetailsComponent,
  expandDetailsParentProps = {},
  summaryComponentProps = {},
}: CustomizedSummaryDetailsProps) {
  const { className: detailsClassName, ...restDetailsProps } = detailsProps;
  const { className: collapseClassName, ...restCollapseDetailsParentProps } =
    collapseDetailsParentProps;
  const { className: summaryClassName, ...restSummaryComponentProps } = summaryComponentProps;
  const { className: expandDetailsClassName, ...restExpandDetailsParentProps } =
    expandDetailsParentProps;

  return (
    <details className={cx(detailsClass, detailsClassName)} {...restDetailsProps}>
      <summary
        data-testid="detail-summary"
        className={cx(summaryClass, summaryClassName)}
        {...restSummaryComponentProps}
      >
        <span
          className={cx('open-summary', expandDetailsClassName)}
          {...restExpandDetailsParentProps}
        >
          {ExpandDetailsComponent}
        </span>
        <span
          className={cx('close-summary', closeSummaryClass, collapseClassName)}
          {...restCollapseDetailsParentProps}
        >
          {CollapseDetailsComponent}
        </span>
      </summary>
      {children}
    </details>
  );
}
